import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiChevronDown } from 'react-icons/fi';
import './PublicNavbar.css';
import logo from '../../images/logo no bg-03.png';
import { Dropdown } from 'react-bootstrap';
import EditProfileCorporate from './nav/EditProfileCorporate';
import EditProfileCorporateUser from './nav/EditProfileCorporateUser';
import LogoutPage from './nav/Logout';

const PRODUCT_LINKS = [
  { label: 'Tasks', to: '/products/task' },
  { label: 'Snap Bill', to: '/products/snap-bill' },
  { label: 'Documents', to: '/products/documents' },
  { label: 'Projects', to: '/products/projects' },
  { label: 'Credentials Vault', to: '/products/credentials-vault' },
  { label: 'Discussion Forum', to: '/products/discussion-forum' },
];

const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Products', to: '/products', children: PRODUCT_LINKS },
  { label: 'Pricing', to: '/pricing' },
  { label: 'About Us', to: '/about-us' },
  { label: 'Contact Us', to: '/contact-us' },
];

const PublicNavbar = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);

    const loadUser = () => {
      const rememberMe = localStorage.getItem('rememberMe') === 'true';
      let accessToken = null;

      if (rememberMe) {
        accessToken = localStorage.getItem('accessToken');
      } else {
        accessToken = sessionStorage.getItem('accessToken');
        const tempAccessToken = localStorage.getItem('tempAccessToken');
        if (tempAccessToken) {
          accessToken = tempAccessToken;
          sessionStorage.setItem('accessToken', tempAccessToken);
        }
      }

      const userData = localStorage.getItem('user');
      if (accessToken && userData) {
        try {
          setUser(JSON.parse(userData));
        } catch (error) {
          console.error('Error parsing user data:', error);
          setUser(null);
        }
      } else {
        setUser(null);
      }
    };

    loadUser();
    const handleStorageChange = (e) => {
      if (e.key === 'user' || e.key === 'accessToken' || e.key === 'logout') {
        loadUser();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (item) => {
    const { pathname, hash } = location;
    if (item.children) {
      return pathname === item.to || pathname.startsWith(`${item.to}/`) ||
        item.children.some((child) => pathname === child.to);
    }
    if (item.to === '/') {
      return pathname === '/' && (!hash || hash === '#' || hash === '');
    }
    if (item.to.startsWith('/#')) {
      return pathname === '/' && hash === item.to.slice(1);
    }
    return pathname === item.to || pathname.startsWith(`${item.to}/`);
  };

  const closeMenu = () => setOpen(false);

  const scrollHomeTarget = (id) => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = reduce ? 'auto' : 'smooth';
    if (!id) {
      window.scrollTo({ top: 0, left: 0, behavior });
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior, block: 'start' });
    }
  };

  const handleNavClick = (item) => (event) => {
    closeMenu();
    if (location.pathname !== '/') {
      return;
    }
    if (item.to === '/') {
      if (!location.hash) {
        event.preventDefault();
        scrollHomeTarget();
      }
      return;
    }
    if (item.to.startsWith('/#')) {
      const id = item.to.slice(2);
      if (location.hash === `#${id}`) {
        event.preventDefault();
        scrollHomeTarget(id);
      }
    }
  };

  return (
    <nav className={`public-navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="public-navbar-container">
        <Link to="/" className="public-navbar-logo" onClick={handleNavClick({ to: '/' })}>
          <img src={logo} alt="flowdo" className="public-navbar-logo-img" />
        </Link>

        <button
          className={`public-navbar-toggle ${open ? 'active' : ''}`}
          onClick={() => setOpen(!open)}
          aria-label="Toggle navigation"
          aria-expanded={open}
        >
          <span className="toggle-line"></span>
          <span className="toggle-line"></span>
          <span className="toggle-line"></span>
        </button>

        <div className={`public-navbar-menu ${open ? 'open' : ''}`}>
          {NAV_LINKS.map((item) => (
            item.children ? (
              <div className="public-navbar-dropdown" key={item.label}>
                <Link
                  to={item.to}
                  className={`public-navbar-link public-navbar-link--dropdown${isActive(item) ? ' is-active' : ''}`}
                  onClick={handleNavClick(item)}
                >
                  {item.label}
                  <FiChevronDown className="public-navbar-caret" aria-hidden="true" />
                </Link>
                <div className="public-navbar-dropdown-menu">
                  {item.children.map((child) => (
                    <Link
                      key={child.to}
                      to={child.to}
                      className={`public-navbar-dropdown-link${location.pathname === child.to ? ' is-active' : ''}`}
                      onClick={closeMenu}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <Link
                key={item.label}
                to={item.to}
                className={`public-navbar-link${isActive(item) ? ' is-active' : ''}`}
                onClick={handleNavClick(item)}
              >
                {item.label}
              </Link>
            )
          ))}
          {user ? (
            <Link
              to="/dashboard"
              className={`public-navbar-link${location.pathname === '/dashboard' ? ' is-active' : ''}`}
              onClick={closeMenu}
            >
              Dashboard
            </Link>
          ) : null}
        </div>

        <div className={`public-navbar-actions ${open ? 'open' : ''}`}>
          {!user ? (
            <>
              <Link to="/login" className="public-navbar-login" onClick={closeMenu}>
                Log In
              </Link>
              <Link to="/register" className="public-navbar-cta" onClick={closeMenu}>
                Get Started
              </Link>
            </>
          ) : (
            <Dropdown
              className="nav-item dropdown header-profile"
              ref={dropdownRef}
              show={dropdownOpen}
              onToggle={() => setDropdownOpen(!dropdownOpen)}
            >
              <Dropdown.Toggle variant="" as="a" className="nav-link i-false c-pointer" role="button">
                <div className="public-navbar-avatar">
                  {(() => {
                    const displayName = user?.name || user?.full_name || user?.FullName || '';
                    return displayName
                      ? displayName.split(' ').map((n) => n[0]).join('').toUpperCase()
                      : '?';
                  })()}
                </div>
              </Dropdown.Toggle>
              <Dropdown.Menu align="right" className="mt-3 mt-lg-0 dropdown-menu dropdown-menu-end">
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee', minWidth: 220 }}>
                  <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 2 }}>
                    {user?.name || user?.full_name || user?.FullName || 'User'}
                  </div>
                  <div style={{ fontSize: 13, color: '#888' }}>{user?.email || ''}</div>
                </div>
                {user?.type === 'corporate' || user?.type === 'user' ? (
                  <>
                    {user.type === 'corporate' ? (
                      <EditProfileCorporate onEdit={() => {}} />
                    ) : (
                      <EditProfileCorporateUser onEdit={() => {}} />
                    )}
                    <Dropdown.Item className="public-dropdown-item" as={Link} to="/dashboard">
                      Dashboard
                    </Dropdown.Item>
                    <Dropdown.Item className="public-dropdown-item" as={Link} to="/changePassword">
                      Change Password
                    </Dropdown.Item>
                  </>
                ) : null}
                <div onClick={() => setUser(null)}>
                  <LogoutPage />
                </div>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      </div>
    </nav>
  );
};

export default PublicNavbar;
